import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Modal, Button, Tag, Textarea, Check, Minus, AlertTriangle } from "@opendate-app/setlist";
import { previewGuests } from "./guestListApi";

const PREVIEW_DELAY_MS = 300;

const listLabel = (listName) => (listName === "House" ? "the house list" : `${listName}’s list`);

const pluralize = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const rowDisplay = (row) => {
  if (row.status === "skipped") {
    return { icon: Minus, tone: "var(--sl-fg-quaternary)", note: "Skipped — doesn’t look like a name", noteTone: "var(--sl-text-tertiary)", muted: true };
  }
  if (row.status === "too_many_plus_ones") {
    return { icon: AlertTriangle, tone: "var(--sl-text-warning)", note: `+${row.plus_ones} is more plus-ones than one guest can bring — not added`, noteTone: "var(--sl-text-warning)", muted: true };
  }
  if (row.status === "over_limit") {
    return { icon: AlertTriangle, tone: "var(--sl-text-warning)", note: "Over the limit for one paste — add these in another paste", noteTone: "var(--sl-text-warning)", muted: true };
  }
  if (row.status === "duplicate") {
    return { icon: AlertTriangle, tone: "var(--sl-text-warning)", note: `Already on ${listLabel(row.existing_list_name)} — skipped`, noteTone: "var(--sl-text-warning)", struck: true };
  }
  return {
    icon: Check,
    tone: "var(--sl-fg-success)",
    note: row.first_name_only ? "First name only — fine for door check-in" : null,
    noteTone: "var(--sl-text-tertiary)",
    tickets: row.plus_ones ? `1 + ${row.plus_ones} · ${row.tickets} tickets` : "1 ticket",
  };
};

const AddGuestsModal = ({ teamId, confirmId, lists, ticketsSold, capacity, manifested, submitting, onClose, onSubmit }) => {
  const [listName, setListName] = useState(lists[0].name);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState({ text: "", listName, rows: [], failed: false });
  const [previewAttempt, setPreviewAttempt] = useState(0);

  useEffect(() => {
    if (!text.trim()) {
      setPreview({ text, listName, rows: [], failed: false });
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      previewGuests({ teamId, confirmId, listName, text })
        .then(({ data }) => { if (!cancelled) setPreview({ text, listName, rows: data.rows, failed: false }); })
        .catch(() => { if (!cancelled) setPreview({ text, listName, rows: [], failed: true }); });
    }, PREVIEW_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [teamId, confirmId, listName, text, previewAttempt]);

  const { rows } = preview;
  const previewIsCurrent = preview.text === text && preview.listName === listName;
  const ready = rows.filter((row) => row.status === "ready");
  const readyTickets = ready.reduce((sum, row) => sum + row.tickets, 0);

  const footer = (
    <div className="d2-guest-modal-footer">
      <div className="d2-guest-modal-summary">
        <strong>{ready.length ? `${pluralize(ready.length, "guest")} · ${pluralize(readyTickets, "ticket")} on ${listLabel(listName)}` : "No guests to add yet"}</strong>
        <span>
          {manifested
            ? `Counts toward capacity: ${ticketsSold} → ${ticketsSold + readyTickets} of ${capacity} · No ticket emails sent`
            : "On top of capacity · No ticket emails sent"}
        </span>
      </div>
      <div className="d2-guest-modal-actions">
        <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="primary" onClick={() => onSubmit({ listName, text })} disabled={submitting || !previewIsCurrent || !ready.length}>
          {ready.length ? `Add ${pluralize(ready.length, "guest")}` : "Add guests"}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal open onClose={onClose} icon={false} size="lg" title="Add to guest list" footer={footer}>
      <div className="d2-guest-modal-body">
        <div className="d2-guest-modal-field">
          <span className="d2-guest-modal-label">Whose list?</span>
          <div className="d2-guest-modal-lists">
            {lists.map((list) => (
              <Tag
                key={list.name}
                selectable
                selected={list.name === listName}
                onSelectedChange={() => setListName(list.name)}
              >
                {list.name === "House" ? "House" : `${list.name} · ${list.role}`}
              </Tag>
            ))}
          </div>
        </div>

        <Textarea
          label="Names"
          rows={8}
          hint="One per line · add +2 for plus-ones. Paste straight from the email — greetings and sign-offs are skipped."
          value={text}
          onChange={(event) => setText(event.target.value)}
        />

        <div className="d2-guest-modal-ticket-type">
          <strong>Guest List · $0</strong>
          <span>Hidden from sale · created automatically for this event</span>
        </div>

        <div className="d2-guest-modal-field">
          <span className="d2-guest-modal-label">
            Preview{rows.length ? ` · ${ready.length} ready` : ""}
          </span>
          {preview.failed && previewIsCurrent ? (
            <div className="d2-guest-preview-error">
              <span>Couldn’t preview these names. Nothing has been added.</span>
              <Button variant="secondary" size="sm" onClick={() => setPreviewAttempt((attempt) => attempt + 1)}>Retry</Button>
            </div>
          ) : rows.length ? (
            <ul className="d2-guest-preview">
              {rows.map((row, index) => {
                const display = rowDisplay(row);
                const Icon = display.icon;
                return (
                  <li key={`${index}-${row.name}`} className={display.muted ? "is-muted" : ""}>
                    <Icon width={16} height={16} style={{ color: display.tone, flexShrink: 0 }} />
                    <div className="d2-guest-preview-name">
                      <span className={display.struck ? "is-struck" : ""}>{row.name}</span>
                      {display.note && <small style={{ color: display.noteTone }}>{display.note}</small>}
                    </div>
                    {display.tickets && <span className="d2-guest-preview-tickets">{display.tickets}</span>}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="d2-guest-preview-empty">Names you paste show up here before anything is added.</p>
          )}
        </div>
      </div>
    </Modal>
  );
};

AddGuestsModal.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  confirmId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  lists: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, role: PropTypes.string })).isRequired,
  ticketsSold: PropTypes.number.isRequired,
  capacity: PropTypes.number.isRequired,
  manifested: PropTypes.bool.isRequired,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default AddGuestsModal;
